import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuestionOverview = ({
  questions,
  currentQuestion,
  answers,
  flaggedQuestions,
  onQuestionSelect,
  isOpen,
  onToggle
}) => {
  const getQuestionStatus = (questionId) => {
    const isAnswered = answers[questionId] !== undefined;
    const isFlagged = flaggedQuestions.includes(questionId);
    const isCurrent = currentQuestion === questionId;

    return { isAnswered, isFlagged, isCurrent };
  };

  const getStatusIcon = (status) => {
    if (status.isCurrent) return 'Circle';
    if (status.isAnswered) return 'CheckCircle';
    return 'Circle';
  };

  const getStatusColor = (status) => {
    if (status.isCurrent) return 'text-primary';
    if (status.isAnswered) return 'text-success';
    return 'text-text-secondary';
  };

  const answeredCount = Object.keys(answers).length;
  const flaggedCount = flaggedQuestions.length;

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:block">
        <div className={`fixed top-0 right-0 h-screen w-[320px] bg-white border-l transition-transform duration-300 transform ${isOpen ? 'translate-x-0' : 'translate-x-full'
          }`} style={{ width: '320px' }}>
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-semibold font-heading text-text-primary">
                Question Overview
              </h3>
              <Button
                variant="ghost"
                iconName="X"
                iconSize={18}
                onClick={onToggle}
              />
            </div>

            {/* Stats */}
            <div className="p-4 border-b border-border">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-success">{answeredCount}</div>
                  <div className="text-xs text-text-secondary">Answered</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-warning">{flaggedCount}</div>
                  <div className="text-xs text-text-secondary">Flagged</div>
                </div>
              </div>
            </div>

            {/* Questions Grid */}
            <div className="flex-1 p-4 overflow-y-auto">
              <div className="grid grid-cols-5 gap-2">
                {questions.map((question) => {
                  const status = getQuestionStatus(question.id);
                  return (
                    <button
                      key={question.id}
                      onClick={() => onQuestionSelect(question.id)}
                      className={`relative w-10 h-10 rounded-lg border-2 flex items-center justify-center text-sm font-medium transition-all duration-200 ${status.isCurrent
                          ? 'border-primary bg-primary text-primary-foreground'
                          : status.isAnswered
                            ? 'border-success bg-success/10 text-success hover:bg-success/20' : 'border-border bg-background text-text-secondary hover:border-border-light hover:bg-border-light'
                        }`}
                    >
                      {question.id}
                      {status.isFlagged && (
                        <div className="absolute flex items-center justify-center w-3 h-3 rounded-full -top-1 -right-1 bg-warning">
                          <Icon name="Flag" size={8} className="text-white" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="mt-6 space-y-2">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 rounded border-primary bg-primary"></div>
                  <span className="text-xs text-text-secondary">Current</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 rounded border-success bg-success/10"></div>
                  <span className="text-xs text-text-secondary">Answered</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 rounded border-border bg-background"></div>
                  <span className="text-xs text-text-secondary">Unanswered</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Flag" size={12} className="text-warning" />
                  <span className="text-xs text-text-secondary">Flagged</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Toggle Button */}
        <button
          onClick={onToggle}
          className={`fixed right-4 top-1/2 -translate-y-1/2 w-10 h-16 bg-primary text-primary-foreground rounded-l-lg shadow-elevation-2 flex items-center justify-center nav-transition z-context-panel`}
        >
          <Icon name={isOpen ? 'ChevronRight' : 'ChevronLeft'} size={16} />
        </button>
      </div>

      {/* Mobile Bottom Sheet */}
      <div className="lg:hidden">
        <Button
          variant="outline"
          iconName="Grid3x3"
          iconSize={18}
          onClick={onToggle}
          className="fixed w-12 h-12 rounded-full bottom-20 right-4 shadow-elevation-2 z-context-panel"
        />

        {isOpen && (
          <div className="fixed inset-0 bg-black/50 z-modal" onClick={onToggle}>
            <div
              className="fixed bottom-0 left-0 right-0 bg-surface rounded-t-lg max-h-[70vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <h3 className="font-semibold font-heading text-text-primary">
                    Question Overview
                  </h3>
                  <Button
                    variant="ghost"
                    iconName="X"
                    iconSize={20}
                    onClick={onToggle}
                  />
                </div>

                {/* Stats */}
                <div className="p-4 border-b border-border">
                  <div className="flex justify-center space-x-8">
                    <div className="text-center">
                      <div className="text-xl font-bold text-success">{answeredCount}</div>
                      <div className="text-xs text-text-secondary">Answered</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-warning">{flaggedCount}</div>
                      <div className="text-xs text-text-secondary">Flagged</div>
                    </div>
                    <div className="text-center">
                      <div className="text-xl font-bold text-text-primary">{questions.length}</div>
                      <div className="text-xs text-text-secondary">Total</div>
                    </div>
                  </div>
                </div>

                {/* Questions Grid */}
                <div className="flex-1 p-4 overflow-y-auto">
                  <div className="grid grid-cols-6 gap-3">
                    {questions.map((question) => {
                      const status = getQuestionStatus(question.id);
                      return (
                        <button
                          key={question.id}
                          onClick={() => {
                            onQuestionSelect(question.id);
                            onToggle();
                          }}
                          className={`relative w-12 h-12 rounded-lg border-2 flex items-center justify-center text-sm font-medium transition-all duration-200 ${status.isCurrent
                              ? 'border-primary bg-primary text-primary-foreground'
                              : status.isAnswered
                                ? 'border-success bg-success/10 text-success' : 'border-border bg-background text-text-secondary'
                            }`}
                        >
                          {question.id}
                          {status.isFlagged && (
                            <div className="absolute flex items-center justify-center w-3 h-3 rounded-full -top-1 -right-1 bg-warning">
                              <Icon name="Flag" size={8} className="text-white" />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default QuestionOverview;